import { FontData } from '@lidojs/design-core';
import { Editor, GetFontQuery, PageControl } from '@lidojs/design-editor';
import axios from 'axios';
import { isArray } from 'lodash';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import AppLayerSettings from '../layout/AppLayerSettings';
import HeaderLayout from '../layout/HeaderLayout';
import Sidebar from '../layout/Sidebar';
import EditorContent from '../pages/EditorContent';
import PreviewModal from '../PreviewModal';
import { SaveModal } from '@lidojs/design-editor';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { doPresign, requestPresign } from '../utils/presign';
import { useTranslation } from 'react-i18next';
import { dataURLtoFile } from '../utils/thumbnail';
import { v4 as uuidv4 } from 'uuid';
import { Buffer } from 'buffer';

type UploadedImage = {
  url: string;
  type: 'svg' | 'image';
};

const Main = () => {
  const leftSidebarRef = useRef<HTMLDivElement>(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [saveModalIsOpen, setSaveModalIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [images, setImages] = useState<UploadedImage[]>([]);
  const location = useLocation();
  const { i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const queryParams = new URLSearchParams(location.search);

  const userLocale = queryParams.get('locale') || 'en';

  useEffect(() => {
    i18n.changeLanguage(userLocale);
  }, [userLocale, i18n]);

  const getFonts = useCallback((query: GetFontQuery) => {
    const buildParams = (data: Record<string, string | string[]>) => {
      const params = new URLSearchParams();

      Object.entries(data).forEach(([key, value]) => {
        if (isArray(value)) {
          value.forEach((v) => params.append(key, v));
        } else {
          params.append(key, value);
        }
      });

      return params;
    };
    return axios
      .get<FontData[]>(`/fonts?${buildParams(query)}`)
      .then((res) => res.data);
  }, []);

  const [viewPortHeight, setViewPortHeight] = useState<number>();
  useEffect(() => {
    const windowHeight = () => {
      setViewPortHeight(window.innerHeight);
    };
    window.addEventListener('resize', windowHeight);
    windowHeight();
    return () => {
      window.removeEventListener('resize', windowHeight);
    };
  }, []);

  const config = useMemo(
    () => ({
      assetPath: './assets',
      frame: {
        defaultImage: {
          url: `./assets/images/frame-placeholder.png`,
          width: 1200,
          height: 800,
        },
      },
    }),
    []
  );

  const handleTabChange = (tab: string | null) => {
    setActiveTab(tab);
  };

  const handleAddImage = async (image: UploadedImage) => {
    setImages((prevState) => {
      return prevState.concat([{ ...image }]);
    });
  };

  const uploadImage = async (file: File, isUpload = false) => {
    // TODO: to integrate with image manipulation then need update this

    if (isUpload) {
      handleTabChange('Upload');
    }

    return new Promise<{ url: string; thumb: string }>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = function () {
        const url = reader.result as string;
        const thumb = reader.result as string;

        if (isUpload) {
          handleAddImage({
            url,
            type: file.type === 'image/svg+xml' ? 'svg' : 'image',
          });
        }

        resolve({
          url,
          thumb,
        });
      };
      reader.onerror = reject;
    });
  };

  const requestForScreenshot = async (uuid) => {
    const response = await axios.post(
      `${
        import.meta.env.VITE_BASE_UPLOADER
      }?url=preview-editor.twibbonize.com/?uuid=${uuid}`
    );

    const { data } = response.data;
    const { filename } = data;

    const frameUuid = filename.replace('.png', '');
    return frameUuid;
  };

  const imageDataUrlToBlob = (dataURL: string): Blob => {
    let arr = dataURL.split(','),
      mime = arr[0].match(/:(.*?);/)![1], // Use non-null assertion operator
      data = arr[1];

    // Decode the base64 data using Buffer
    const buffer = Buffer.from(data, 'base64');

    // Create a Blob from the buffer
    return new Blob([buffer], {
      type: mime,
    });
  };

  const ensureHttps = (url) => {
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  const onSaveTemplate = async (dataURL: string, serverSide: boolean) => {
    try {
      setIsLoading(true);
      const accessToken = queryParams.get('idtoken');
      const type = queryParams.get('type') || 'frame';
      const campaignUrl = queryParams.get('campaignUrl');
      const deeplink = queryParams.get('deeplink');
      const targetHost = queryParams.get('target') || import.meta.env.VITE_BASE_TWB;

      if (serverSide) {
        console.log('is server side')
        const blob = new Blob([dataURL], { type: 'text/plain' });
        const fileSize = blob.size;
        const uuid = uuidv4();
        const key = uuid + '.txt';

        const url = `${import.meta.env.VITE_BASE_PRESIGN}/editor-txt`;
        const presignData = await requestPresign(
          url,
          { fileSize, key },
          accessToken
        );
        const filename = await doPresign(presignData, blob);

        const frameUuid = await requestForScreenshot(uuid);

        const safeTargetHost = ensureHttps(targetHost);
        const targetUrl = campaignUrl
          ? `${safeTargetHost}/campaign/${campaignUrl}/${filename}`
          : `${safeTargetHost}/create/${type}/${filename}?deeplink=${deeplink}`;

        window.location.href = targetUrl;
      } else {
        console.log('is client side')
        const blob = imageDataUrlToBlob(dataURL);
        const fileSize = blob.size;
        const key = uuidv4() + '.png';
        const url = `${import.meta.env.VITE_BASE_PRESIGN}/temp`;
        const presignData = await requestPresign(
          url,
          { fileSize, key },
          accessToken
        );

        const filename = await doPresign(presignData, blob);

        const safeTargetHost = ensureHttps(targetHost);
        const targetUrl = campaignUrl
          ? `${safeTargetHost}/campaign/${campaignUrl}/${filename}`
          : `${safeTargetHost}/create/${type}/${filename}?deeplink=${deeplink}`;

        window.location.href = targetUrl;
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }



    // const targetUrl = campaignUrl ? `${import.meta.env.VITE_BASE_TWB}/campaign/${campaignUrl}/${filename}` : `${import.meta.env.VITE_BASE_TWB}/create/${type}/${filename}`;
    // window.location.href = targetUrl;
  };

  const openSaveModal = () => {
    setSaveModalIsOpen(true);
  };

  // useEffect(() => {
  //   if (!queryParams.has('idtoken')) {
  //     window.location.href = import.meta.env.VITE_BASE_TWB;
  //   }
  // }, [queryParams]);

  return (
    <Editor config={config} getFonts={getFonts} uploadImage={uploadImage}>
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          width: '100vw',
          height: '100vh',
          maxHeight: viewPortHeight ? `${viewPortHeight}px` : 'auto',
        }}
      >
        <HeaderLayout
          openPreview={() => setOpenPreview(true)}
          openSaveModal={openSaveModal}
          loading={isLoading}
        />

        {openPreview && <PreviewModal onClose={() => setOpenPreview(false)} />}

        {saveModalIsOpen && (
          <SaveModal
            isOpen={saveModalIsOpen}
            onClose={() => setSaveModalIsOpen(false)}
            onSaveTemplate={onSaveTemplate}
          />
        )}

        <div
          css={{
            display: 'flex',
            flexDirection: 'row',
            flex: 'auto',
            overflow: 'auto',
            background: '#EBECF0',
            '@media (max-width: 1024px)': {
              flexDirection: 'column-reverse',
            },
          }}
        >
          <div
            ref={leftSidebarRef}
            css={{
              display: 'flex',
              background: 'white',
            }}
          >
            <Sidebar
              activeTab={activeTab}
              onTabChange={handleTabChange}
              images={images}
              onAddImage={handleAddImage}
            />
          </div>
          <div
            css={{
              flexGrow: 1,
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
            }}
          >
            <AppLayerSettings />
            <div
              css={{
                flexGrow: 1,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <EditorContent />
            </div>
            <div
              css={(theme) => ({
                height: 40,
                background: theme.colors.background,
                borderTop: '1px solid rgba(57,76,96,.15)',
                display: 'grid',
                alignItems: 'center',
                flexShrink: 0,
                '@media (max-width: 1024px)': {
                  display: 'none',
                },
              })}
            >
              <PageControl />
            </div>
          </div>
        </div>
      </div>

      <ToastContainer position="bottom-center" hideProgressBar />
    </Editor>
  );
};

export default Main;
